import { useRef, useState, useCallback, useEffect } from "react";
import client from "@/lib/api";
import { getAudioEl, unlockAudioEl } from "@/lib/audio";

// Module-level cache shared across all steps so audio is fetched once.
const b64Cache = new Map();     // key -> base64 mp3 string
const inflight = new Map();     // key -> Promise<string> (dedupes concurrent fetches)

const keyFor = (text, lang) => `${lang}|${text}`;

async function fetchB64(text, lang) {
  const k = keyFor(text, lang);
  if (b64Cache.has(k)) return b64Cache.get(k);
  if (inflight.has(k)) return inflight.get(k);
  const p = client.post("/learn/tts", { text, lang })
    .then(({ data }) => { b64Cache.set(k, data.audio); inflight.delete(k); return data.audio; })
    .catch((e) => { inflight.delete(k); throw e; });
  inflight.set(k, p);
  return p;
}

// Warm the cache ahead of time so a later tap plays instantly.
export function prefetchTts(text, lang = "de") {
  if (!text) return;
  fetchB64(text, lang).catch(() => {});
}

// Plays TTS via an HTMLAudioElement (media channel). On iOS this ignores the
// hardware mute/ring switch, unlike the Web Audio API which is silenced by it.
export function useTts() {
  const reqRef = useRef(0);
  const [playing, setPlaying] = useState(null);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);

  const stop = useCallback(() => {
    reqRef.current += 1;
    const el = getAudioEl();
    if (el) { try { el.pause(); } catch {} }
    if (typeof window !== "undefined" && window.speechSynthesis) { try { window.speechSynthesis.cancel(); } catch {} }
    setPlaying(null);
    setLoading(null);
  }, []);

  const play = useCallback(async (text, lang = "de", key = null) => {
    const id = key || text;
    if (!text) return;
    const myReq = ++reqRef.current;
    // Unlock + grab the shared element WITHIN the tap gesture (critical for iOS).
    unlockAudioEl();
    const el = getAudioEl();
    if (el) { try { el.pause(); } catch {} }
    if (typeof window !== "undefined" && window.speechSynthesis) { try { window.speechSynthesis.cancel(); } catch {} }
    setError(null);
    setPlaying(null);
    const cached = b64Cache.get(keyFor(text, lang));
    if (!cached) setLoading(id);
    try {
      const b64 = cached || await fetchB64(text, lang);
      if (myReq !== reqRef.current) return;
      if (!el || !b64) throw new Error("no audio");
      el.src = `data:audio/mpeg;base64,${b64}`;
      try { el.currentTime = 0; } catch {}
      el.onended = () => { if (myReq === reqRef.current) setPlaying(null); };
      setLoading(null);
      setPlaying(id);
      await el.play();
    } catch {
      if (myReq !== reqRef.current) return;
      setLoading(null);
      // Fallback: browser speech engine so the learner still hears something.
      const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
      if (synth) {
        try {
          synth.cancel();
          const u = new SpeechSynthesisUtterance(text);
          u.lang = lang === "de" ? "de-DE" : "en-US";
          u.rate = 1.0;
          u.onend = () => { if (myReq === reqRef.current) setPlaying(null); };
          u.onerror = () => { if (myReq === reqRef.current) { setPlaying(null); setError(id); } };
          setError(null);
          setPlaying(id);
          synth.speak(u);
          return;
        } catch {}
      }
      setPlaying(null);
      setError(id);
    }
  }, []);

  const prefetch = useCallback((text, lang = "de") => prefetchTts(text, lang), []);

  useEffect(() => stop, [stop]);

  return { play, stop, prefetch, playing, loading, error };
}
